import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { Toaster } from "sonner";

import Login from "@/pages/Login";
import Wishlist from "@/pages/Wishlist";
import Deals from "@/pages/Deals";
import Brands from "@/pages/Brands";
import QuickAdd from "@/pages/QuickAdd";
import Mobile from "@/pages/Mobile";
import AdminLayout from "@/pages/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import AdminCatalog from "@/pages/admin/Catalog";
import AdminArchive from "@/pages/admin/Archive";
import AddProduct from "@/pages/admin/AddProduct";
import AdminOrders from "@/pages/admin/Orders";
import AdminClients from "@/pages/admin/Clients";
import AdminDesigns from "@/pages/admin/DesignRequests";
import Crawlers from "@/pages/admin/Crawlers";
import BulkImport from "@/pages/admin/BulkImport";

import ClientLayout from "@/pages/client/ClientLayout";
import Shop from "@/pages/client/Shop";
import MyOrders from "@/pages/client/MyOrders";
import ClientDesigns from "@/pages/client/DesignRequests";

const Guard = ({ role, children }) => {
    const { user, loading } = useAuth();
    if (loading) return <div className="h-screen flex items-center justify-center font-body text-sm">Loading…</div>;
    if (!user) return <Navigate to="/login" replace />;
    if (role && user.role !== role) return <Navigate to={user.role === "admin" ? "/admin" : "/shop"} replace />;
    return children;
};

const Landing = () => {
    const { user, loading } = useAuth();
    if (loading) return <div className="h-screen flex items-center justify-center font-body text-sm">Loading…</div>;
    if (!user) return <Navigate to="/login" replace />;
    // Owner (admin login) lands on the Price Radar. B2B routes still exist for legacy data.
    return <Navigate to={user.role === "admin" ? "/wishlist" : "/shop"} replace />;
};

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <CartProvider>
                        <Toaster position="bottom-right" richColors closeButton />
                        <Routes>
                            <Route path="/" element={<Landing />} />
                            <Route path="/login" element={<Login />} />
                            <Route
                                path="/wishlist"
                                element={
                                    <Guard role="admin">
                                        <Wishlist />
                                    </Guard>
                                }
                            />
                            <Route
                                path="/deals"
                                element={
                                    <Guard role="admin">
                                        <Deals />
                                    </Guard>
                                }
                            />
                            <Route
                                path="/brands"
                                element={
                                    <Guard role="admin">
                                        <Brands />
                                    </Guard>
                                }
                            />
                            <Route
                                path="/quick-add"
                                element={
                                    <Guard role="admin">
                                        <QuickAdd />
                                    </Guard>
                                }
                            />
                            <Route
                                path="/mobile"
                                element={
                                    <Guard role="admin">
                                        <Mobile />
                                    </Guard>
                                }
                            />

                            <Route
                                path="/admin"
                                element={
                                    <Guard role="admin">
                                        <AdminLayout />
                                    </Guard>
                                }
                            >
                                <Route index element={<Dashboard />} />
                                <Route path="catalog" element={<AdminCatalog />} />
                                <Route path="archive" element={<AdminArchive />} />
                                <Route path="add" element={<AddProduct />} />
                                <Route path="bulk" element={<BulkImport />} />
                                <Route path="crawlers" element={<Crawlers />} />
                                <Route path="orders" element={<AdminOrders />} />
                                <Route path="clients" element={<AdminClients />} />
                                <Route path="designs" element={<AdminDesigns />} />
                            </Route>

                            <Route
                                path="/"
                                element={
                                    <Guard role="client">
                                        <ClientLayout />
                                    </Guard>
                                }
                            >
                                <Route path="shop" element={<Shop />} />
                                <Route path="my-orders" element={<MyOrders />} />
                                <Route path="designs" element={<ClientDesigns />} />
                            </Route>
                        </Routes>
                    </CartProvider>
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
